/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */
import { ChangeEvent } from 'react';
import filter from 'lodash/filter';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import {
    Heading,
    ContentContainerBodyShift,
    FlexRow,
    FlexCol,
    Stack,
    Badge,
    useBreakpoint,
    Text,
    Inline,
    Select,
    TextBlock,
    TextField,
    Icon,
} from 'sarsaparilla';
import ReactTableWithLayout from 'shared-ui/components/ReactTableWithLayout/ReactTableWithLayout';
import { lotteryStatisticsDescription } from 'site-kit';
import { getYearsForSelect } from 'ui-permit/src/utils/utilsNew';

interface LotteryStatisticsProps {
    lotteryStatistics?: any[];
    handleSelectChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
    selectVal?: string;
    lotteryStatisticsLoading?: boolean;
    globalFilterState?: string;
    setGlobalFilterState: (value: string) => void;
}

export default function LotteryStatistics({
    lotteryStatistics = [],
    handleSelectChange,
    selectVal,
    lotteryStatisticsLoading,
    globalFilterState,
    setGlobalFilterState,
}: LotteryStatisticsProps) {
    let lotteryStatisticsDataFiltered = lotteryStatistics;
    const { isMdAndDown } = useBreakpoint();
    //First year of lotteries according to GG
    const SELECT_START = '2019';

    if (
        !isNil(globalFilterState) &&
        !isEmpty(lotteryStatistics) &&
        !lotteryStatisticsLoading
    ) {
        lotteryStatisticsDataFiltered = filter(
            lotteryStatisticsDataFiltered,
            (lottery) => {
                const normalizedFilterVal = globalFilterState?.toLowerCase();
                const normalizedFacilityName = lottery.facility_name?.toLowerCase();
                return normalizedFacilityName.includes(normalizedFilterVal);
            }
        );
    }

    interface Row {
        claim_rate: number;
        close_at: string;
        facility_name: string;
        lottery_type: string;
        lottery_year: string;
        num_of_applications: number;
        num_of_awarded: number | string;
        num_of_cancellations: number;
        num_of_claimed: number;
        num_of_declined: number;
        num_of_no_response: number;
        open_at: string;
        permit_id: string;
        reservation_end_date: string;
        reservation_start_date: string;
        success_rate: number;
    }

    interface AllTableData {
        column: any;
        getCellProps: any;
        render: any;
        row: any;
        value: string;
    }

    interface AdditionalTableData {
        allCells: Array<any>;
        cells: Array<any>;
        depth: number;
        getRowProps: any;
        original: Row;
    }

    const renderFacilityNameWithLotteryTypeBadge = (row: Row) => {
        return (
            <Stack space="none">
                {row.facility_name}
                <Inline>
                    <Badge style={{ textTransform: 'none' }}>
                        <Text fontWeight="semibold">{row.lottery_type}</Text>
                    </Badge>
                </Inline>
            </Stack>
        );
    };

    const renderNumApplications = (row: Row) => {
        return get(row, 'num_of_applications', 0).toLocaleString();
    };

    const renderNumAwarded = (row: Row) => {
        return get(row, 'num_of_awarded', 0).toLocaleString();
    };
    const renderSuccessRate = (row: Row) => {
        return `${(row.success_rate * 100).toFixed(1)}%`;
    };

    const getColorFromRedToGreenByPercentage = (value: number) => {
        const hue = Math.round(value) + 4;
        return ['hsla(', hue, ', 65%, 53%, 0.5)'].join('');
    };

    const getCellBackgroundColor = (cellInfo: AllTableData) => {
        const cellValue = Number(cellInfo.value.split('%')[0]);
        return getColorFromRedToGreenByPercentage(cellValue);
    };

    const facilityNameSort = (a: AdditionalTableData, b: AdditionalTableData) => {
        let aUpdated = a.original.facility_name.replace('(OLD) ', '');
        aUpdated = a.original.facility_name.replace('(NEW) ', '');
        let bUpdated = b.original.facility_name.replace('(OLD) ', '');
        bUpdated = b.original.facility_name.replace('(NEW) ', '');

        if (aUpdated > bUpdated) {
            return 1;
        }
        if (aUpdated < bUpdated) {
            return -1;
        }
        return 0;
    };

    //These extra methods for numApplicationSort and numAwardsSort are present so the sort works as expected when commas are added to the numbers
    const numApplicationsSort = (a: AdditionalTableData, b: AdditionalTableData) => {
        const aUpdated = parseInt(String(a.original.num_of_applications), 10);
        const bUpdated = parseInt(String(b.original.num_of_applications), 10);
        if (aUpdated > bUpdated) {
            return 1;
        }
        if (aUpdated < bUpdated) {
            return -1;
        }
        return 0;
    };

    const numAwardsSort = (a: AdditionalTableData, b: AdditionalTableData) => {
        const aUpdated = parseInt(String(a.original.num_of_awarded), 10);
        const bUpdated = parseInt(String(b.original.num_of_awarded), 10);
        if (aUpdated > bUpdated) {
            return 1;
        }
        if (aUpdated < bUpdated) {
            return -1;
        }
        return 0;
    };

    const columns = isMdAndDown
        ? [
              {
                  Header: 'Facility Name',
                  id: 'facility_name',
                  accessor: 'facility_name',
                  sortable: true,
                  Cell: ({ row }: AllTableData) =>
                      renderFacilityNameWithLotteryTypeBadge(row.original),
                  sortType: facilityNameSort,
              },
              {
                  Header: 'Application Success Rate',
                  id: 'success_rate',
                  accessor: (row: Row) => renderSuccessRate(row),
                  sortable: true,
                  getCellBackgroundColor: (row: AllTableData) =>
                      getCellBackgroundColor(row),
                  maxWidth: 230,
              },
          ]
        : [
              {
                  Header: 'Facility Name',
                  id: 'facility_name',
                  accessor: 'facility_name',
                  sortable: true,
                  sortType: facilityNameSort,
              },
              {
                  Header: 'Type',
                  id: 'lottery_type',
                  accessor: 'lottery_type',
                  sortable: true,
                  maxWidth: 150,
              },
              {
                  Header: 'Number of Applications',
                  id: 'num_of_applications',
                  accessor: (d: Row) => renderNumApplications(d),
                  sortable: true,
                  maxWidth: 150,
                  sortType: numApplicationsSort,
              },
              {
                  Header: 'Number of Awards',
                  id: 'num_of_awarded',
                  accessor: (d: Row) => renderNumAwarded(d),
                  sortable: true,
                  maxWidth: 150,
                  sortType: numAwardsSort,
              },
              {
                  Header: 'Application Success Rate',
                  id: 'success_rate',
                  accessor: (d: Row) => renderSuccessRate(d),
                  sortable: true,
                  getCellBackgroundColor: (row: AllTableData) =>
                      getCellBackgroundColor(row),
                  maxWidth: 160,
              },
          ];

    return (
        <section id="lottery-statistics" className="mt-6 mb-6">
            <ContentContainerBodyShift>
                <FlexRow hasGutters={false}>
                    <FlexCol>
                        <Heading
                            headingLevel={2}
                            hasUnderline="left"
                            appearance="h3"
                            className="lottery-section-header"
                        >
                            Lottery Statistics
                        </Heading>
                    </FlexCol>
                </FlexRow>

                <Stack space="md">
                    <TextBlock width="xl" alignX="left">
                        {lotteryStatisticsDescription}
                    </TextBlock>
                    <FlexRow
                        justifyContent="space-between"
                        alignItems="center"
                        hasGutters={false}
                    >
                        <FlexCol sm={4} className={isMdAndDown ? 'pb-2' : undefined}>
                            <TextField
                                label="Search by Facility Name"
                                id="filter-list"
                                iconElement={<Icon iconName="search" />}
                                hasClearButton
                                value={globalFilterState}
                                onChange={(e) => setGlobalFilterState(e.target.value)}
                            />
                        </FlexCol>

                        <FlexCol sm={4}>
                            <FlexRow justifyContent="end">
                                <Select
                                    className="lottery-year-dropdown mr-0"
                                    id="lottery-year-dropdown"
                                    label="Historical Application Success Rates For *"
                                    options={getYearsForSelect(SELECT_START)}
                                    value={selectVal}
                                    onChange={handleSelectChange}
                                    isInline
                                    gaTrackingId="035267057689"
                                />
                            </FlexRow>
                        </FlexCol>
                    </FlexRow>

                    <ReactTableWithLayout
                        data={lotteryStatisticsDataFiltered}
                        columns={columns}
                        showRowCount
                        hasPagination
                        showPageSize={false}
                        defaultSorted={
                            [{ id: 'facility_name', ascending: true }] as never[]
                        }
                        isStriped={false}
                        className={'lotteryStatisticsTable'}
                        hasExternalFilter={true}
                        globalFiler={globalFilterState}
                        gaTrackingId="316559979043"
                        isLoading={lotteryStatisticsLoading}
                        aria-label="Facility lottery statistics"
                    />

                    <FlexRow>
                        <FlexCol>
                            <ul className="lottery-statistics">
                                <li>
                                    Daily lottery data is the sum of all daily lottery
                                    applications for a location. Daily application success
                                    rates will vary over the lottery timeframe. For
                                    example, the total applications for &quot;Angels
                                    Landing: Summer (Hikes from June 1 - Aug 31)&quot; is
                                    the sum of all applications submitted to the daily
                                    lotteries for the selected year for hikes during the
                                    period of June 1 - Aug 31.
                                </li>
                                <li>
                                    The total &quot;Number of Awards&quot; is defined as
                                    the number of successful applicants which is
                                    determined by the managing agency along with all
                                    associated business rules.
                                </li>
                            </ul>
                        </FlexCol>
                    </FlexRow>
                </Stack>
            </ContentContainerBodyShift>
        </section>
    );
}
